import React from 'react';
import styled from 'styled-components';
import StyledButton from "../components/ui/StyledButton";
import {
    Section,
    SectionTitle,
    SubHeadline,
    Placeholder,
    Highlight,
    FeatureItem, FeatureTitle, FeaturesGrid,
    HeroSection,
    PageTitle, FeatureDescription,
    FigureWrapper, FigureCaption,
} from '../components/ui/PageElements';
import {
    RoadmapListItem, StyledList,
} from '../components/ui/ListStyles';
import ResponsiveImage from '../components/ui/ResponsiveImage';
import PageMeta from '../components/PageMeta';

const ProjectWrapper = styled.div`
    max-width: 1200px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    position: relative;
`;

// Styled ResponsiveImage to remove border and background
const FigureImage = styled(ResponsiveImage)`
    border: none;
    background-color: transparent;

    img {
        border: none;
        background-color: transparent;
    }
`;

// Specific styles for AlmondO
const ChallengeBlock = styled.div`
    background-color: rgba(0,0,0, 0.5);
    border: 1px solid var(--grid-color);
    padding: 30px;
    margin: 20px auto;
    max-width: 800px;
    text-align: center;

    p {
        font-size: 18px;
        font-style: italic;
        color: #cccccc;
        margin-bottom: 15px;
    }

    span {
        font-size: 24px;
        font-weight: bold;
        color: var(--primary-color);
        display: block; /* Put on new line */
        margin-top: 10px;
        margin-bottom: 10px;
    }
`;

// MIDI Controls Table Style
const ControlsTable = styled.table`
    width: 100%;
    max-width: 800px;
    margin: 30px auto;
    border-collapse: collapse;
    font-size: 14px;
    text-align: left;

    th, td {
        border: 1px solid var(--grid-color);
        padding: 10px 12px;
        color: var(--text-muted);
    }

    th {
        background-color: rgba(255, 51, 153, 0.1);
        color: var(--primary-color);
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: 13px;
    }

    tr:nth-child(even) {
        background-color: rgba(0,0,0, 0.2);
    }

    code { /* Style CC numbers */
        color: var(--primary-color);
        font-weight: bold;
    }
`;

const NameOriginText = styled.p`
    font-size: 12px;
    color: #888888;
    text-align: center;
    margin-top: -20px; /* Pull up slightly */
    margin-bottom: 40px;
    font-style: italic;
`;



const ShrimpText = styled.span`
    font-size: 16px;
    display: block; /* Place on its own line */
    margin-top: 10px;
    color: #999;
`;


const AlmondoProjectPage = () => {
    const gitlabProjectUrl = "https://gitlab.com/lperron/almondo"; // Adjust if needed
    const drawbarsImageUrl = "/assets/Almondo/hammonddrawbars.gif";

    return (
        <ProjectWrapper>
            <PageMeta
                title="Almondo"
                description="Almondo - A tonewheel organ emulator running on STM32F4. 9 drawbars, vibrato, and MIDI control, all on a microcontroller. DIY embedded audio."
                canonicalPath="/projects/almondo"
            />
            {/* --- Hero Section --- */}
            <HeroSection>
                <PageTitle>AlmondO</PageTitle>
                <NameOriginText>
                    (Originally Almond V0.random → Almond0 → "Oh AlmondO sounds nice and friendly!")
                </NameOriginText>
                <SubHeadline>
                    A DIY Tonewheel Organ emulation running entirely on an STM32F4 Discovery board. Built from scratch with SPL libraries, wrestling sine waves and MIDI with low-level programming.
                </SubHeadline>
                <FigureWrapper>
                    <FigureImage src={"/assets/projects/almondoPreview.png"} alt="Our dear Almondo playind the organ!" style={{ maxHeight: '300px', objectFit: 'contain' }} />
                </FigureWrapper>
                <StyledButton href={gitlabProjectUrl} target="_blank" rel="noopener noreferrer">
                    View on GitLab
                </StyledButton>
            </HeroSection>

            {/* --- The Challenge Section --- */}
            <Section>
                <SectionTitle>The 109-Wheel Question</SectionTitle>
                <ChallengeBlock>
                    <p>"How the hell can we run 109 floating point sine generators at 48KHz on a Cortex-M4 MCU running at 168 MHz without hardware sine support?"</p>
                    <span>SPOILER: We can't.</span>
                    <ShrimpText>But here's a shrimp 🦐 (Disclaimer: This code is completely shrimp-free)</ShrimpText>
                </ChallengeBlock>
                <SubHeadline style={{ maxWidth: '900px', margin: '40px auto 0 auto', textAlign: 'center' }}>
                    Generating the rich sound of a tonewheel organ requires simulating numerous simultaneous sine waves (wheels). Doing this naively on constrained hardware like the STM32F4 is... optimistic.
                </SubHeadline>
            </Section>

            {/* --- The Solution: Engine Core --- */}
            <Section>
                <SectionTitle>Engine Core // LUTs & Drawbars</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    Instead of impossible real-time sine calculations, AlmondO uses heavily optimized <Highlight>Look-Up Tables (LUTs)</Highlight>. We only store tables for the 12 notes of the sub-octave. Then, <Highlight>109 pointers</Highlight> read these tables at different speeds (doubling speed for each octave) to generate all required frequencies.
                    <br /><br />
                    Think of it like having only 12 static wheels, but <Highlight>many magnetic heads</Highlight> spinning around them at various speeds. This makes the impossible, possible (and avoids needing shrimps).
                    <br /><br />
                    Of course, 109 raw sine waves sound... intense. Like classic <a href="https://en.wikipedia.org/wiki/Hammond_organ" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>Hammond organs</a>, AlmondO uses <Highlight>9 virtual drawbars</Highlight> to control the mix of 9 distinct harmonics for each note played. Each drawbar offers 128 steps of MIDI resolution.
                </SubHeadline>
                <FigureWrapper>
                    <div style={{ display: 'block', width: 'fit-content', maxWidth: '100%', margin: '0 auto' }}>
                        <FigureImage src={drawbarsImageUrl} alt="Hammond organ drawbars showing which note each drawbar corresponds to" style={{ maxHeight: '300px', objectFit: 'contain', display: 'block' }} />
                    </div>
                    <FigureCaption>Hammond organ drawbars: each drawbar controls a specific harmonic (footage) in the tonewheel sound.</FigureCaption>
                </FigureWrapper>
            </Section>

            {/* --- Features Section ("Nice Additions") --- */}
            <Section>
                <SectionTitle>Nice Additions // Extra Flavor</SectionTitle>
                <FeaturesGrid>
                    <FeatureItem>
                        <FeatureTitle>Harmonic Leakage</FeatureTitle>
                        <FeatureDescription>
                            Simulates the subtle signal bleed between "bin-mate" wheels found in real tonewheel organs (packed 4 octaves apart). Adds authenticity. Controllable amount & on/off switch (MIDI CC or User Button).
                        </FeatureDescription>
                    </FeatureItem>
                    <FeatureItem>
                        <FeatureTitle>Vibrato</FeatureTitle>
                        <FeatureDescription>
                            It wouldn't be a tonewheel organ without vibrato! AlmondO includes a time-varying delay-based vibrato. (Adds one more sine generator to the pile!)
                        </FeatureDescription>
                    </FeatureItem>
                    <FeatureItem>
                        <FeatureTitle>Glitchy Disto</FeatureTitle>
                        <FeatureDescription>
                            Not a traditional distortion, but an effect born from a signed/unsigned casting bug! Creates interesting high harmonics. Can sound organic with soft ADSR, or aggressive. Tame with an external LPF if needed.
                        </FeatureDescription>
                    </FeatureItem>
                    <FeatureItem>
                        <FeatureTitle>ADSR Envelope</FeatureTitle>
                        <FeatureDescription>
                            Okay, real organs don't have ADSR. But I like pads too much! Adds sound shaping possibilities beyond the typical organ percussion. Sounds great! 🤩
                        </FeatureDescription>
                    </FeatureItem>
                </FeaturesGrid>
            </Section>

            {/* --- Controls Section --- */}
            <Section>
                <SectionTitle>MIDI Control Matrix</SectionTitle>
                <SubHeadline>AlmondO is fully MIDI controlled. Default CC mappings are below (editable in `midiControls.h`):</SubHeadline>
                <ControlsTable>
                    <thead>
                    <tr>
                        <th>Control</th>
                        <th>Function</th>
                        <th>Default CC#</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr><td>MIDI Channel</td><td>MIDI Input Channel</td><td><code>1</code></td></tr>
                    <tr><td>Sustain Pedal</td><td>Sustain (Hold)</td><td><code>64</code></td></tr>
                    <tr><td>Sostenuto Pedal</td><td>Sostenuto</td><td><code>66</code></td></tr>
                    <tr><td>Glitchy Distortion</td><td>Effect Amount</td><td><code>28</code></td></tr>
                    <tr><td>ADSR Level</td><td>Main Volume</td><td><code>86</code></td></tr>
                    <tr><td>ADSR Attack</td><td>Attack Time</td><td><code>87</code></td></tr>
                    <tr><td>ADSR Decay</td><td>Decay Time</td><td><code>90</code></td></tr>
                    <tr><td>ADSR Sustain</td><td>Sustain Level</td><td><code>77</code></td></tr>
                    <tr><td>ADSR Release</td><td>Release Time</td><td><code>78</code></td></tr>
                    <tr><td>Drawbar 1-9</td><td>Level of Nth Harmonic</td><td><code>106, 109, 110, 111, 112, 115, 118, 119, 75</code></td></tr>
                    <tr><td>Leakage Amount</td><td>Leakage Level [0-0.2]</td><td><code>29</code></td></tr>
                    <tr><td>Mod Wheel</td><td>Vibrato Level</td><td><code>1</code></td></tr>
                    <tr><td>Leakage Switch</td><td>On (≥64) / Off (inf 64) <br/><i>(Also toggled by User Button)</i></td><td><code>31</code></td></tr>
                    </tbody>
                </ControlsTable>
            </Section>

            {/* --- Performance Section --- */}
            <Section>
                <SectionTitle>Performance & Polyphony</SectionTitle>
                <SubHeadline>
                    AlmondO achieves <Highlight>18-note polyphony</Highlight> without harmonic leakage enabled, and <Highlight>12-note polyphony</Highlight> with leakage active. This is generally sufficient for single-keyboard organ playing, but long ADSR releases combined with leakage can push the limits.
                </SubHeadline>
            </Section>

            {/* --- Roadmap/TODO Section --- */}
            <Section>
                <SectionTitle>Project Status & Roadmap</SectionTitle>
                <SubHeadline>AlmondO is functional but always evolving. Here's the current focus and future wishlist:</SubHeadline>
                <StyledList>
                    <RoadmapListItem><strong>Vibrato Enhancement:</strong> Aiming for more expressive vibrato (varying speed/amount, ramp-up time), inspired by Yamaha Motif behaviour.</RoadmapListItem>
                    <RoadmapListItem><s><strong>Full MIDI Control:</strong> Add MIDI CC for leakage on/off switch.</s> (Done!)</RoadmapListItem>
                    <RoadmapListItem><strong>Polyphony++:</strong> <s>Achieve 12 voices with leakage.</s> (Done!) Ideally reach 16/24 voices, though this is a stretch goal.</RoadmapListItem>
                    <RoadmapListItem><strong>Custom Hardware:</strong> Design a dedicated interface with physical drawbars (slide pots!) for intuitive control and 12-bit precision. (Need to find nice, cheap pots!)</RoadmapListItem>
                    <RoadmapListItem><s><strong>USB MIDI:</strong> Implement USB MIDI input. (USB is scary 👻)</s> (Done) .</RoadmapListItem>
                    <RoadmapListItem><strong>MIDILib Creation:</strong> Refactor the MIDI handling into a reusable library for future STM32 projects.</RoadmapListItem>
                    <RoadmapListItem><strong>Custom PCB ("Discovery Free"):</strong> Design a minimal board with just the necessary components (MCU, DAC, MIDI input, etc.) - requires tackling LQFP soldering!</RoadmapListItem>
                </StyledList>
            </Section>

            {/* --- Final CTA Section --- */}
            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>Explore The Code</SectionTitle>
                <SubHeadline>
                    Dive into the source code, check out the MIDI implementation, or maybe even try building it yourself!
                </SubHeadline>
                <StyledButton href={gitlabProjectUrl} target="_blank" rel="noopener noreferrer">
                    View on GitLab
                </StyledButton>
            </Section>

        </ProjectWrapper>
);
};

export default AlmondoProjectPage;